// fetch wrapper for the admin contract (01-api.md §3.M). Adapted from web/src/core/api.ts.
//
// Auth model (05-admin §1.4 / §2.2):
//   1. Perimeter — Cloudflare Access sends its JWT as a cookie automatically;
//      `credentials: "include"` forwards it to /v1/admin/*.
//   2. App level — backend re-checks users.role ∈ {moderator, admin}.
// For local dev (no Access perimeter) an operator-supplied bearer token is
// attached instead (see core/tokens.ts).
import { API_BASE } from "./config.js";
import { loadDevToken } from "./tokens.js";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, detail?: string) {
    super(detail ?? code);
    this.status = status;
    this.code = code;
  }
}

interface ProblemJson {
  status?: number;
  code?: string;
  detail?: string;
  title?: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  const dev = loadDevToken();
  if (dev) headers["authorization"] = `Bearer ${dev}`;

  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    // Forward Cloudflare Access cookies in production.
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!resp.ok) {
    let problem: ProblemJson = {};
    try {
      problem = (await resp.json()) as ProblemJson;
    } catch {
      /* non-json error (e.g. Access HTML challenge) */
    }
    throw new ApiError(resp.status, problem.code ?? `HTTP_${resp.status}`, problem.detail ?? problem.title);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

// True when the endpoint is not (yet) implemented by the API (404).
// Used to degrade gracefully to a "엔드포인트 준비중" state instead of a hard error.
export function isNotReady(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

// Map an error to a short human label for empty/error states.
export function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return "인증 필요 (401) — Cloudflare Access 재인증 또는 dev 토큰 확인";
    if (e.status === 403) return "권한 부족 (403 FORBIDDEN) — moderator/admin 역할 필요";
    if (e.status === 404) return "엔드포인트 없음 (404) — 어드민 API가 아직 구현 전일 수 있습니다";
    if (e.status === 451) return "법적 차단 (451) — 격리 콘텐츠는 전용 감사 미리보기로만 열람";
    return `${e.status} ${e.code}${e.message && e.message !== e.code ? ` — ${e.message}` : ""}`;
  }
  return String(e);
}
